import React, { useState } from 'react';
import { Send, Image, FileText, Mic, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import API from '../api/axios';
import { useWhatsApp } from '../context/WhatsAppContext';

const SendMessage = () => {
  const { status } = useWhatsApp();
  const [number, setNumber] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [type, setType] = useState('text'); // 'text' | 'image' | 'document' | 'audio'

  const isConnected = status === 'connected';

  const handleSend = async () => {
    if (!number.trim()) return;
    setSending(true);
    setResult(null);

    try {
      if (type === 'text') {
        if (!message.trim()) {
          setResult({ success: false, error: 'Message cannot be empty' });
          return;
        }
        const res = await API.post('/send-message', { number: number.trim(), message });
        setResult({ success: true, messageId: res.data.messageId });
        setMessage('');
      } else {
        if (!file) {
          setResult({ success: false, error: 'Please select a file' });
          return;
        }
        const formData = new FormData();
        formData.append('number', number.trim());

        let url = '/send-document';
        if (type === 'image') {
          formData.append('image', file);
          if (message) formData.append('caption', message);
          url = '/send-image';
        } else if (type === 'audio') {
          formData.append('audio', file);
          url = '/send-audio';
        } else {
          formData.append('document', file);
        }

        const res = await API.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setResult({ success: true, messageId: res.data.messageId });
        setFile(null);
        setMessage('');
      }
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setSending(false);
    }
  };

  const messageTypes = [
    { id: 'text', label: 'Text', icon: <Send size={16} /> },
    { id: 'image', label: 'Image', icon: <Image size={16} /> },
    { id: 'document', label: 'Document', icon: <FileText size={16} /> },
    { id: 'audio', label: 'Audio', icon: <Mic size={16} /> },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
              <Send size={20} className="text-white" />
            </div>
            Send Message
          </h1>
          <p className="mt-2 text-slate-500">Send text, images, documents, or audio to any WhatsApp number.</p>
        </div>

        {/* Connection warning */}
        {!isConnected && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700">
            <AlertCircle size={20} className="flex-shrink-0" />
            <p className="text-sm font-medium">
              WhatsApp is not connected. Please{' '}
              <a href="/generate-qr" className="underline font-semibold">link your device</a>{' '}
              first.
            </p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 space-y-5">
          {/* Phone number */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. 919876543210 (with country code, no +)"
              value={number}
              onChange={(e) => setNumber(e.target.value.replace(/\D/g, ''))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-300 focus:border-green-400 transition"
            />
            <p className="mt-1 text-xs text-gray-400">Include country code without + (e.g. 91 for India)</p>
          </div>

          {/* Message type selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message Type</label>
            <div className="flex gap-2 flex-wrap">
              {messageTypes.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setType(t.id); setFile(null); setResult(null); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    type === t.id
                      ? 'bg-green-500 text-white border-green-500 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:text-green-600'
                  }`}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Text message */}
          {type === 'text' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={5}
                placeholder="Type your message here…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-300 focus:border-green-400 transition resize-none"
              />
              <p className="mt-1 text-xs text-gray-400 text-right">{message.length} characters</p>
            </div>
          )}

          {/* Image upload */}
          {type === 'image' && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Image <span className="text-red-500">*</span>
              </label>
              <label className="block cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-400 transition">
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-700">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); setFile(null); }}
                      className="ml-auto p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <Image size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Click to select an image</p>
                    <p className="text-xs mt-1">JPG, PNG, GIF, WebP</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Caption (optional)</label>
                <input
                  type="text"
                  placeholder="Add a caption…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-300 transition"
                />
              </div>
            </div>
          )}

          {/* Document upload */}
          {type === 'document' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Document <span className="text-red-500">*</span>
              </label>
              <label className="block cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-400 transition">
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                      <FileText size={24} className="text-blue-500" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); setFile(null); }}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <FileText size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Click to select a document</p>
                    <p className="text-xs mt-1">PDF, DOC, XLS, ZIP, etc.</p>
                  </div>
                )}
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
            </div>
          )}

          {/* Audio upload */}
          {type === 'audio' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Audio <span className="text-red-500">*</span>
              </label>
              <label className="block cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-400 transition">
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                      <Mic size={24} className="text-purple-500" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); setFile(null); }}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <Mic size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Click to select an audio file</p>
                    <p className="text-xs mt-1">MP3, OGG, WAV, M4A</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`flex items-start gap-3 p-4 rounded-xl border ${
              result.success
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {result.success
                ? <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                : <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              }
              <div>
                <p className="text-sm font-semibold">
                  {result.success ? 'Message sent successfully!' : 'Failed to send'}
                </p>
                {result.success && result.messageId && (
                  <p className="text-xs mt-0.5 opacity-70">ID: {result.messageId}</p>
                )}
                {!result.success && (
                  <p className="text-xs mt-0.5">{result.error}</p>
                )}
              </div>
            </div>
          )}

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={sending || !number.trim() || !isConnected}
            className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send size={18} />
                Send {type === 'text' ? 'Message' : type.charAt(0).toUpperCase() + type.slice(1)}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendMessage;
